import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

// ── DTOs ────────────────────────────────────────────────────────────

export interface ContabilidadMdoItem {
  mdo_nombre: string;
  mdo_unidad: string;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

export interface ContabilidadDescuentoItem {
  descripcion: string;
  monto: number;
}

export interface ContabilidadSvItem {
  descripcion: string;
  monto: number;
}

export interface ContabilidadRubro {
  descripcion: string;
  unidad: string;
  cantidad: number;
  precio_unitario: number;
  precio_total: number;
  retencion_pct: number;
  retenido: number;
  a_pagar: number;
  mdo_items?: ContabilidadMdoItem[];
  desc_items?: ContabilidadDescuentoItem[];
  sv_items?: ContabilidadSvItem[];
}

export interface ContabilidadProyecto {
  project_id: string;
  project_nombre: string;
  rubros: ContabilidadRubro[];
  subtotal_rubros: number;
  total_extras: number;
  total_descuentos: number;
  total_retenido: number;
  total_a_pagar: number;
}

export interface ContabilidadMaestro {
  maestro_id: string;
  maestro_nombre: string;
  maestro_cedula: string;
  proyectos: ContabilidadProyecto[];
  gran_total: number;
}

export interface ReporteContabilidadDTO {
  desde: string;
  hasta: string;
  maestros: ContabilidadMaestro[];
  total_general: number;
}

// ── Hooks ────────────────────────────────────────────────────────────

export function useContabilidadReporte(desde: string, hasta: string) {
  return useQuery({
    queryKey: queryKeys.contabilidad.reporte(desde, hasta),
    queryFn: async () => {
      const response = await api.get<{ data: ReporteContabilidadDTO }>(
        `/contabilidad/reporte?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`,
      );
      return response.data.data;
    },
    enabled: !!desde && !!hasta,
  });
}

export function useContabilidadPdfGeneral() {
  return useMutation({
    mutationFn: async ({ desde, hasta }: { desde: string; hasta: string }) => {
      const res = await api.post<{ html?: string; download_url?: string }>(
        '/contabilidad/reporte/pdf',
        { desde, hasta },
      );
      return res.data;
    },
    onSuccess: (data) => {
      if (data.html) {
        const blob = new Blob([data.html], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
      } else if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    },
  });
}

export function useContabilidadPdfMaestro() {
  return useMutation({
    mutationFn: async ({
      desde,
      hasta,
      maestroId,
    }: {
      desde: string;
      hasta: string;
      maestroId: string;
    }) => {
      const res = await api.post<{ html?: string; download_url?: string }>(
        '/contabilidad/reporte/pdf',
        { desde, hasta, maestroId },
      );
      return res.data;
    },
    onSuccess: (data) => {
      if (data.html) {
        const blob = new Blob([data.html], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
      } else if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    },
  });
}
